import { useCallback, useMemo } from 'react';

import { VerifiedGroupStatus } from '@sg/graphql-schema';

import { type TelemetryEventPayload, useTelemetry } from '@order/Telemetry';

import type { IdMeAuthorizationResult } from '../../idme.types';
import type { VerifiedGroups } from '../../machine/setup';
import type * as Actions from '../../machine/setup/actions';

// ─────────────────────────────────────────────────────────────────────────────

export const useIdMeVerificationTracking = (params: Params) => {
  const { entrySource } = params;

  const { track, setCustomerProperty } = useTelemetry();

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const setVerifiedUserProperties = useCallback(
    (verifiedGroups: VerifiedGroups) => {
      const activeGroup = verifiedGroups.find(checkIfGroupActive);

      setCustomerProperty('idme_verified', activeGroup !== undefined);

      if (!activeGroup) {
        return;
      }

      setCustomerProperty('idme_verified_date', activeGroup.verifiedAt);
      setCustomerProperty('idme_affiliation', activeGroup.group);
      setCustomerProperty('idme_verification_expires', activeGroup.expiresAt);
    },
    [setCustomerProperty],
  );

  const trackVerifiedGroups = useCallback(
    (_args: unknown, actionParams: VerifiedGroupsParams) => {
      setVerifiedUserProperties(actionParams.verifiedGroups);
    },
    [setVerifiedUserProperties],
  );

  const trackVerifyTapped = useCallback(() => {
    track('idme.verification_page.cta_tapped', { entry_source: entrySource });
  }, [entrySource, track]);

  const trackVerificationStarted = useCallback(() => {
    track('idme.verify.started', { entry_source: entrySource });
  }, [entrySource, track]);

  const trackVerificationSucceeded = useCallback(
    (_args: unknown, actionParams: VerificationResultParams) => {
      const { verificationResult } = actionParams;

      if (verificationResult.__typename !== 'VerifyGroupSuccess') {
        return;
      }

      const { verifiedGroups } = verificationResult;
      const affiliation = verifiedGroups.filter(checkIfGroupActive).map(getGroupName).join(',');

      track('idme.verify.success', { entry_source: entrySource, affiliation });
      setVerifiedUserProperties(verifiedGroups);
    },
    [entrySource, setVerifiedUserProperties, track],
  );

  const trackVerificationFailed = useCallback(
    (_args: unknown, actionParams: FailureReasonParams) => {
      track('idme.verify.failure', {
        entry_source: entrySource,
        failure_reason: actionParams.failureReason,
      });
    },
    [entrySource, track],
  );

  const trackAuthorizationFailure = useCallback(
    (_args: unknown, actionParams: AuthorizationResultParams) => {
      track('idme.verify.failure', {
        entry_source: entrySource,
        failure_reason: getAuthorizationFailureReason(actionParams.result),
      });
    },
    [entrySource, track],
  );

  const trackVerificationAbandoned = useCallback(() => {
    track('idme.verify.abandoned', { entry_source: entrySource });
  }, [entrySource, track]);

  const trackAccountConflict = useCallback(() => {
    track('idme.verify.account_conflict', { entry_source: entrySource });
  }, [entrySource, track]);

  // ───────────────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      trackVerifiedGroups,
      trackVerifyTapped,
      trackVerificationStarted,
      trackVerificationSucceeded,
      trackVerificationFailed,
      trackAuthorizationFailure,
      trackVerificationAbandoned,
      trackAccountConflict,
    }),
    [
      trackVerifiedGroups,
      trackVerifyTapped,
      trackVerificationStarted,
      trackVerificationSucceeded,
      trackVerificationFailed,
      trackAuthorizationFailure,
      trackVerificationAbandoned,
      trackAccountConflict,
    ],
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfGroupActive(verifiedGroup: VerifiedGroups[number]) {
  return verifiedGroup.status === VerifiedGroupStatus.Active;
}

function getGroupName(verifiedGroup: VerifiedGroups[number]) {
  return verifiedGroup.group;
}

function getAuthorizationFailureReason(result: IdMeAuthorizationResult) {
  if (result.status === 'failed') {
    return result.code;
  }

  return 'missing_authorization_request';
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  entrySource: TelemetryEventPayload<'idme.verify.started'>['entry_source'];
};

type VerifiedGroupsParams = Parameters<typeof Actions.trackVerifiedGroups>[1];

type VerificationResultParams = Parameters<typeof Actions.trackVerificationSucceeded>[1];

type FailureReasonParams = Parameters<typeof Actions.trackVerificationFailed>[1];

type AuthorizationResultParams = Parameters<typeof Actions.trackAuthorizationFailure>[1];
